import { useState } from 'react';
import { cn } from '@web/lib/shadcn';
import { trpc } from '@web/lib/trpc';
import { Route } from '@web/routes/dashboard/admin/contact/details/$contactId';
import * as Tabs from '@radix-ui/react-tabs';

import ContactNotFound from './ContactNotFound';
import ContactSkeleton from './ContactSkeleton';
import { ContactWithDetails } from '@shared/types/ContactWithDetails';
import ContactInfo from './contacts-details-tabs/ContactInfo';
import Comments from './contacts-details-tabs/Comments';
import Contracts from './contacts-details-tabs/Contracts';
import Payments from './contacts-details-tabs/Payments';
import { Dock, MessageSquareText, ReceiptText, SquareUser } from 'lucide-react';
import { CommentAddOrEditDialog } from '@web/routes/dashboard/chat/-components/comment-add-or-edit';
import { Route as ContactRouter } from '@web/routes/dashboard/admin/contact/route';
import { Link } from '@tanstack/react-router';
import { Button } from '@web/components/ui/button';
import { ArrowLeftCircleIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@web/components/ui/avatar';

import { ScrollArea } from '@web/components/ui/scroll-area';

export default function ContactDetailsView() {
  const { activeCommentId, setActiveCommentId } =
    ContactRouter.useRouteContext();

  const [tabSelected, setTabSelected] = useState<
    'info' | 'comments' | 'contracts' | 'payments'
  >('info');

  const { contactId } = Route.useParams();
  const {
    data: contact,
    isError,
    isFetching,
  } = trpc.contact.getContact.useQuery(
    { contactId },
    {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  );

  if (isError) return <ContactNotFound id={contactId} />;
  if (isFetching) return <ContactSkeleton />;
  if (!contact) return null;

  return (
    <ScrollArea
      className="h-[calc(100vh_-_var(--desktop-header-height))]"
      viewportClassName="block"
    >
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Back button in a clean header */}
        <div className="mb-6">
          <Link
            to="/dashboard/admin/contact/list"
            search={(current: URLSearchParams) => current}
          >
            {({ isActive }: { isActive: boolean }) => (
              <Button
                variant="outline"
                className={cn(
                  'border-primary/20 text-primary hover:bg-primary/5 hover:border-primary/30 rounded-full',
                  isActive && 'bg-primary/10 hover:bg-primary/15',
                )}
              >
                <ArrowLeftCircleIcon className="mr-2 h-4 w-4" />
                Back to contacts
              </Button>
            )}
          </Link>
        </div>

        {/* Main tabs container with modern styling */}
        <div className="overflow-hidden rounded-xl bg-white shadow-md">
          <Tabs.Root
            defaultValue="contact-info"
            value={tabSelected === 'info' ? 'contact-info' : tabSelected}
            onValueChange={(value) => {
              if (value === 'contact-info') {
                setTabSelected('info');
              } else {
                setTabSelected(value as 'comments' | 'contracts' | 'payments');
              }
            }}
          >
            {/* Header with user info and responsive tabs */}
            <div className="border-b">
              <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 sm:py-5 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center">
                  <Avatar className="border-primary/10 size-16 shrink-0 border-2">
                    <AvatarImage
                      src={`/placeholder.svg?text`}
                      alt={`${contact.firstName} ${contact.lastName}`}
                    />
                    <AvatarFallback className="bg-primary text-lg text-white">
                      {contact.firstName?.charAt(0) || ''}
                      {contact.lastName?.charAt(0) || ''}
                    </AvatarFallback>
                  </Avatar>
                  <div className="ml-4">
                    <h1 className="text-xl font-bold text-gray-900">{`${contact.firstName || ''} ${contact.lastName || ''}`}</h1>
                    <p className="text-sm text-gray-500">
                      {contact.email ?? 'No email provided'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {contact.phoneNumber ?? 'No phone provided'}
                    </p>
                  </div>
                </div>

                {/* Status badge would go here */}
                <div className="hidden md:block">
                  <span className="bg-primary/10 text-primary inline-flex items-center rounded-full px-3 py-1 text-sm font-medium">
                    Active Customer
                  </span>
                </div>
              </div>

              {/* Mobile tab dropdown */}
              <div className="block border-t border-gray-100 px-4 py-3 md:hidden">
                <div className="relative">
                  <select
                    className="focus:border-primary focus:ring-primary w-full appearance-none rounded-md border border-gray-200 bg-white py-2 pl-3 pr-10 text-sm font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-1"
                    value={tabSelected}
                    onChange={(e) => {
                      const value = e.target.value as
                        | 'info'
                        | 'comments'
                        | 'contracts'
                        | 'payments';
                      setTabSelected(value);
                      // Map the dropdown value to the actual tab value
                      const tabValueMap = {
                        info: 'contact-info',
                        comments: 'comments',
                        contracts: 'contracts',
                        payments: 'payments',
                      };

                      // Use the onValueChange via state update instead of direct DOM manipulation
                      // This is more reliable and avoids TypeScript errors
                      // Just updating the state to match the desired tab is sufficient
                      // The Tabs.Root value binding will handle activating the correct tab
                    }}
                  >
                    <option value="info">Contact Info</option>
                    <option value="comments">Comments</option>
                    <option value="contracts">Contracts</option>
                    <option value="payments">Payments</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Desktop tabs - hidden on mobile */}
              <div className="hidden border-t border-gray-100 md:block">
                <Tabs.List className="flex px-1 font-medium text-gray-500">
                  <Tabs.Trigger
                    value="contact-info"
                    className={cn(
                      'hover:text-primary hover:border-primary/30 flex items-center border-b-2 border-transparent px-4 py-3 transition-all',
                      tabSelected == 'info'
                        ? 'text-primary border-primary'
                        : '',
                    )}
                    onClick={() => {
                      setTabSelected('info');
                    }}
                  >
                    <SquareUser className="mr-2 h-4 w-4" />
                    <span>Contact Info</span>
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="comments"
                    className={cn(
                      'hover:text-primary hover:border-primary/30 flex items-center border-b-2 border-transparent px-4 py-3 transition-all',
                      tabSelected == 'comments'
                        ? 'text-primary border-primary'
                        : '',
                    )}
                    onClick={() => {
                      setTabSelected('comments');
                    }}
                  >
                    <MessageSquareText className="mr-2 h-4 w-4" />
                    <span>Comments</span>
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="contracts"
                    className={cn(
                      'hover:text-primary hover:border-primary/30 flex items-center border-b-2 border-transparent px-4 py-3 transition-all',
                      tabSelected == 'contracts'
                        ? 'text-primary border-primary'
                        : '',
                    )}
                    onClick={() => {
                      setTabSelected('contracts');
                    }}
                  >
                    <ReceiptText className="mr-2 h-4 w-4" />
                    <span>Contracts</span>
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="payments"
                    className={cn(
                      'hover:text-primary hover:border-primary/30 flex items-center border-b-2 border-transparent px-4 py-3 transition-all',
                      tabSelected == 'payments'
                        ? 'text-primary border-primary'
                        : '',
                    )}
                    onClick={() => {
                      setTabSelected('payments');
                    }}
                  >
                    <Dock className="mr-2 h-4 w-4" />
                    <span>Payments</span>
                  </Tabs.Trigger>
                </Tabs.List>
              </div>
            </div>

            {/* Tab content area with consistent padding */}
            <div className="p-1">
              <Tabs.Content value="contact-info" className="p-0 outline-none">
                <ContactInfo contact={contact as ContactWithDetails} />
              </Tabs.Content>

              <Tabs.Content value="comments" className="p-0 outline-none">
                <Comments contactId={contactId} />
              </Tabs.Content>

              <Tabs.Content value="contracts" className="p-0 outline-none">
                <Contracts contactId={contactId} />
              </Tabs.Content>

              <Tabs.Content value="payments" className="p-0 outline-none">
                <Payments contactId={contactId} />
              </Tabs.Content>
            </div>
          </Tabs.Root>
        </div>
      </div>

      {activeCommentId !== null && (
        <CommentAddOrEditDialog
          key={activeCommentId}
          commentId={activeCommentId}
          onClose={() => {
            setActiveCommentId(null);
          }}
        />
      )}
    </ScrollArea>
  );
}
